import { AcreditacionConfig } from './acreditacion.model';

export interface Configuration {
  id?: string;
  creationDate?: string | null;
  creationUser?: string | null;
  lastActualization?: string | null;
  companyId: string;
  general: GeneralConfig;
  supplierClassification: SupplierClassification[];
  dataRequest: DataRequest[];
  documentRequest: DocumentRequest[];
  cards: Card[];
  version?: number;
  sistema?: SistemaConfig | null;
  acreditacion?: AcreditacionConfig | null;
  altaAutomatica?: AltaAutomaticaConfig | null;
}

export interface SistemaConfig { periodoFichaMeses: number; diasAviso: number[]; }

/** Umbrales estrictos ("más de"); null = ese umbral no se usa. */
export interface AltaAutomaticaConfig { activa: boolean; minTransacciones: number | null; minMontoMM: number | null; }

export const PERIODOS_FICHA = [3, 6, 12, 24, 36, 48];
export const periodoFicha = (c: Pick<Configuration, 'sistema'> | null | undefined) => c?.sistema?.periodoFichaMeses ?? 12;

/** Fecha (yyyy-MM-dd, hora local) de hace `meses` meses; el 31 de marzo menos 1 mes es el 28/29 de febrero. */
export function desdeMeses(meses: number, hoy = new Date()): string {
  const fin = new Date(hoy.getFullYear(), hoy.getMonth() - meses + 1, 0).getDate();
  const d = new Date(hoy.getFullYear(), hoy.getMonth() - meses, Math.min(hoy.getDate(), fin));
  const dos = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
}

export interface GeneralConfig {
  notificationMail: string;
  language: string;
  timezone: string;
  flags: Flag[];
  cardSections: CardSection[];
  /** Roles que ven cada menú; un menú sin entrada lo ven todos. */
  menus?: PermisoMenu[];
  /** Roles con cada acción de la ficha; sin entrada rigen los roles por defecto de ACCIONES_FICHA. */
  acciones?: PermisoAccion[];
  /** Campos fijos de la empresa (emp_fono, emp_mail) exigidos en la ficha y la acreditación; no en el alta. */
  camposEmpresaObligatorios?: string[];
}

export interface PermisoMenu { menuId: string; ver: string[]; }
export interface PermisoAccion { accionId: string; roles: string[]; }

export const MENU_SOCIOS = 'socios';
/** Acciones de la ficha que se asignan por rol (Admin siempre las tiene), con los menús que piden para llegar a ejecutarlas. */
export const ACCIONES_FICHA = [
  { id: 'reasignar', label: 'Reasignar clase', descripcion: 'Mover al socio a otra clase y cambiar su ficha', porDefecto: ['Gestor'], menus: [MENU_SOCIOS] },
  { id: 'acreditacion', label: 'Gestionar acreditación', descripcion: 'Extender, revisar y decidir acreditaciones (iniciarlas es solo del Admin)', porDefecto: ['Gestor', 'Gerencia'], menus: [MENU_SOCIOS, 'acreditaciones'] },
] as const;

/** Menús que se configuran por rol; Configuración es solo de Admin. El orden define a dónde se redirige si falta permiso. */
export const MENUS = [
  { id: 'home_registro', label: 'Mi Registro · Mis Socios de Negocios', ruta: '/pages/home/registro' },
  { id: 'home_transacciones', label: 'Mi Registro · Resumen de Transacciones', ruta: '/pages/home/transacciones' },
  { id: 'home_evaluaciones', label: 'Mi Registro · Resumen de Evaluaciones', ruta: '/pages/home/evaluaciones' },
  { id: 'socios', label: 'Mis Socios de Negocio', ruta: '/pages/business-partners' },
  { id: 'directorio', label: 'Directorio', ruta: '/pages/directorio' },
  { id: 'acreditaciones', label: 'Acreditaciones', ruta: '/pages/acreditaciones' },
] as const;
export const MENU_CONFIGURACION = 'configuracion';
export const MENUS_HOME: readonly string[] = ['home_registro', 'home_transacciones', 'home_evaluaciones'];

export interface Flag {
  flagId: string;
  flagName: string;
  flagDescription: string;
  flagColor: string;
  enabled: boolean
}

export interface CardSection {
  sectionId: string;
  sectionName: string;
  sectionDescription: string;
  enabled: boolean;
  system?: boolean;
  /** La completa una integración externa: protegida y de solo lectura. */
  externo?: boolean;
  /** Acción de la sección: cargar (null), riesgo, anotar o ninguna (solo lectura). */
  accion?: AccionSeccion | null;
  /** Bloque fijo de la ficha (obligatoria sin ítems configurables). */
  bloque?: boolean;
  /** null/ausente: sección previa a los permisos, todos los roles la ven y cargan. */
  permisos?: PermisosSeccion | null;
  /** Texto de ayuda bajo el título (ficha interna y portal del proveedor). */
  ayuda?: string | null;
  /** false: solo el socio la completa (portal); los internos solo la ven. */
  enRepresentacion?: boolean | null;
  /** ClassIds de último nivel en que se colabora; vacía o sin valor = todas las clases cuya ficha incluye la sección. */
  colaboracionClases?: string[] | null;
  /** Obsoleta: la renovación es por documento (DocumentRequest.periodicity). */
  periodicidad?: PeriodicidadSeccion | null;
  /** Solo si se colabora: lo que envía el socio espera la aprobación de estos usuarios. Se cambia aparte (solo Admin). */
  aprobacion?: AprobacionSeccion | null;
}

export interface AprobadorSeccion { usuario: string; nombre: string; email: string; }
export interface AprobacionSeccion { requiere: boolean; aprobadores: AprobadorSeccion[]; }

export type AccionSeccion = 'cargar' | 'riesgo' | 'anotar' | 'ninguna';
export type PeriodicidadSeccion = 'mensual' | 'trimestral' | 'semestral' | 'anual';

export const PERIODICIDADES_SECCION: { value: PeriodicidadSeccion; label: string; meses: number; adverbio: string }[] = [
  { value: 'mensual', label: 'Mensual', meses: 1, adverbio: 'mensualmente' }, { value: 'trimestral', label: 'Trimestral', meses: 3, adverbio: 'trimestralmente' },
  { value: 'semestral', label: 'Semestral', meses: 6, adverbio: 'semestralmente' }, { value: 'anual', label: 'Anual', meses: 12, adverbio: 'anualmente' },
];

/** Ids de las secciones del sistema (las crea la API). */
export const SECCIONES_FIJAS = {
  general: 'sis_general', riesgo: 'sis_riesgo', bitacora: 'sis_bitacora', comercial: 'datos_basicos', edp: 'sis_edp',
  contactos: 'sis_contactos', representantes: 'sis_representantes', transacciones: 'sis_transacciones', historial: 'sis_historial',
} as const;

/** Secciones del sistema que se activan/inactivan y se eligen por ficha; las demás están en toda ficha. */
export const FIJAS_OPCIONALES: readonly string[] = [SECCIONES_FIJAS.riesgo, SECCIONES_FIJAS.bitacora, SECCIONES_FIJAS.edp, SECCIONES_FIJAS.representantes];

/** Acepta ítems de la Biblioteca (definición iConstruye, no la cambia el administrador). */
export const editable = (s: Pick<CardSection, 'bloque' | 'externo'>) => !s.bloque && !s.externo;

/** La ficha tipo incluye la sección del sistema opcional (activa y no excluida). */
export const incluida = (c: Pick<Card, 'sections'> | null | undefined, s: Pick<CardSection, 'sectionId' | 'enabled'>) =>
  s.enabled && !(c?.sections ?? []).some(x => x.sectionId === s.sectionId && x.excluida);

/** `sinVer` más las secciones del sistema opcionales inactivas o que no incluye la ficha tipo de la clase del socio. */
export function seccionesOcultas(sinVer: ReadonlySet<string>, config: Pick<Configuration, 'cards' | 'general'> | null | undefined,
  clase: string | null | undefined): ReadonlySet<string> {
  const ficha = (config?.cards ?? []).find(c => c.enabled && (c.associatedClassifications ?? []).includes(clase ?? ''));
  const fuera = (config?.general?.cardSections ?? []).filter(s => FIJAS_OPCIONALES.includes(s.sectionId) && !incluida(ficha, s)).map(s => s.sectionId);
  return fuera.length ? new Set([...sinVer, ...fuera]) : sinVer;
}

type DocumentoVigencia = { url?: string | null; lastUpdated?: string | Date | null; fechaVencimiento?: string | null };

/** Igual que la API: lo primero entre la última carga (o aprobación) + la frecuencia y la fecha de vencimiento del archivo; null sin archivo. */
export function venceDocumento(d: DocumentoVigencia, periodicidad: string | null | undefined): Date | null {
  if (!d.url) return null;
  const meses = PERIODICIDADES_SECCION.find(p => p.value === periodicidad)?.meses;
  const desde = d.lastUpdated ? new Date(d.lastUpdated) : null;
  const porFrecuencia = meses && desde && desde.getTime() > 0 ? new Date(desde.setMonth(desde.getMonth() + meses)) : null;
  const porFecha = d.fechaVencimiento && /^\d{4}-\d{2}-\d{2}/.test(d.fechaVencimiento) ? new Date(d.fechaVencimiento.slice(0, 10) + 'T00:00:00Z') : null;
  return porFrecuencia && porFecha ? (porFrecuencia < porFecha ? porFrecuencia : porFecha) : porFrecuencia ?? porFecha;
}

/** Documentos de la sección del socio ya vencidos; periodicidad = frecuencia de cada documento de la Biblioteca. */
export function vencidos<T extends DocumentoVigencia & { documentoSolicitaId: string }>(documentos: T[] | null | undefined,
  periodicidad: (docId: string) => string | null | undefined, hoy = new Date()): T[] {
  return (documentos ?? []).filter(d => (venceDocumento(d, periodicidad(d.documentoSolicitaId))?.getTime() ?? Infinity) < hoy.getTime());
}

/** Texto del permiso de acción de una sección; null si la sección es de solo lectura. */
export function etiquetaAccion(s: Pick<CardSection, 'accion' | 'externo'>): string | null {
  if (s.externo || s.accion === 'ninguna') return null;
  return s.accion === 'riesgo' ? 'Solicitar informe de riesgo' : s.accion === 'anotar' ? 'Registrar anotaciones' : 'Cargar/Editar';
}

export type AccesoProveedor = 'none' | 'view' | 'edit';

export interface PermisosSeccion {
  ver: string[];
  cargar: string[];
  proveedor: AccesoProveedor;
}

export interface RolRegistro {
  id: string;
  nombre: string;
}

export interface MiAcceso {
  roles: string[];
  admin: boolean;
  gestor?: boolean;
  rolesResueltos: boolean;
  puedeReasignar?: boolean;
  /** Admin o un rol con la acción "Gestionar acreditación": extiende y decide acreditaciones; iniciarlas es solo del Admin. */
  gestionaAcreditacion?: boolean;
  secciones: Record<string, { ver: boolean; cargar: boolean }>;
  /** Menús visibles para el usuario (vacío si no se pudo confirmar su rol). */
  menus?: string[];
}

export interface SocioDeClase { id: string; rut: string; nombre?: string | null; }

export interface ResultadoReasignacion { loteId: string; movidos: number; omitidos: { socioId: string; motivo: string }[]; }

export interface FichaEnReasignacion { clase?: string | null; fichaId?: string | null; fichaVersion?: number | null; }

/** `foto`: JSON con { Estado, ClasificacionProveedor, Ficha } tal como estaba antes del cambio (sin las secciones que no puedes ver). */
export interface Reasignacion {
  id: string; socioId: string; rut: string; nombre?: string | null; loteId: string; origen: 'individual' | 'masivo'; motivo: string;
  de: FichaEnReasignacion; a: FichaEnReasignacion; foto: string; usuario: string; fechaUtc: string;
}

export interface SupplierClassification {
  classId: string;
  className: string;
  classDescription: string;
  classColor: string;
  enabled?: boolean | null;
  childs: SupplierClassification[];
  /** null = sin workflow de acreditación. */
  acreditacion?: AcreditacionClase | null;
}

export interface AcreditacionClase {
  enabled: boolean;
  plazoDias: number;
  /** Usuarios Gestor (sin el sufijo _idEmpresa). */
  responsables: string[];
}

export interface DataRequest {
  fieldId: string;
  fieldMatrixId: string;
  fieldName: string;
  placeholder: string;
  sectionCardId: string;
  fieldType: string;
  fieldOptions: FieldOption[];
  config: FieldConfig | null;
  description?: string | null;
  updatePeriodicity?: string | null;
  extraSectionIds?: string[] | null;
  label?: string | null;
  help?: string | null;
  requiredForAll?: boolean;
  enabled?: boolean | null;
  system?: boolean;
  /** Lo completa una integración externa: fijo y opcional en toda ficha. */
  externo?: boolean;
  /** Clave del catálogo de sugeridos: se activa y se hace obligatorio, pero no se edita ni elimina. */
  sugerido?: string | null;
}

export interface FieldOption {
  optionId: string;
  optionValue: string;
}

export interface FieldConfig {
  length?: string;
  validation?: string;
}

export interface DocumentRequest {
  requestedDocId: string;
  docName: string;
  docDescription: string;
  documentMatrixId: string;
  sectionCardId: string;
  allowedTypes: string[];
  maxWeight: string;
  requestsExpiration: boolean;
  periodicity: string;
  extraSectionIds?: string[] | null;
  label?: string | null;
  help?: string | null;
  requiredForAll?: boolean;
  enabled?: boolean | null;
  system?: boolean;
  placeholder?: string | null;
  sugerido?: string | null;
}

export interface Card {
  cardId: string;
  cardName: string;
  cardDescription: string;
  associatedClassifications: string[];
  sections: CardSectionData[];
  validatorRole?: string | null;
  enabled: boolean;
  version?: number;
}

export interface CardSectionData {
  sectionId: string;
  validatorRole?: string | null; // Nuevo atributo - rol de validación por sección
  /** Sección del sistema opcional que esta ficha no incluye. */
  excluida?: boolean;
  data: {
    fieldId: string,
    status: 'required' | 'optional' | 'not_used';
  }[];
  documents: {
    docId: string;
    status: 'required' | 'optional' | 'not_used';
  }[];
}

export type TipoConfig = 'clases' | 'secciones' | 'datos' | 'documentos' | 'fichas' | 'etiquetas';

/** Paso que falta para completar una acción (409 de la API); el asistente lo muestra con su botón. */
export interface PasoPendiente {
  codigo: 'MoverSocios' | 'InactivarSubclase' | 'QuitarClases' | 'QuitarDeFicha' | 'ItemSinOtraSeccion'
    | 'QuitarObligatorioGlobal' | 'ActivarPadre' | 'AsignarFicha' | 'NoPermitido' | 'MoverItem';
  mensaje: string;
  entidadId?: string | null;
  nombre?: string | null;
  cantidad?: number | null;
}

export interface AuditoriaConfig {
  modulo: string;
  accion: string;
  entidadId: string;
  entidadNombre?: string | null;
  antes?: string | null;
  despues?: string | null;
  usuario: string;
  fechaUtc: string;
}

export const SECCION_BASICA = 'datos_basicos';
export const activo = (x: { enabled?: boolean | null }) => x.enabled !== false;
/** Las fichas solo se asignan a clases de último nivel (sin subclases activas). */
export const ultimoNivel = (c: { childs?: { enabled?: boolean | null }[] | null }) => !(c.childs ?? []).some(activo);

/**
 * Socios del cliente según su clase (sociosPorClase usa "" para los que no tienen). Sin clase: la suya no está en el árbol.
 * Sin ficha: su clase no está en una ficha activa (misma regla que FichaDeClase en la API).
 */
export function conteoSocios(cfg: Pick<Configuration, 'supplierClassification' | 'cards'>, socios: Record<string, number>) {
  const clases = new Set<string>();
  const recorrer = (cs: SupplierClassification[] | null | undefined) => (cs ?? []).forEach(c => { clases.add(c.className); recorrer(c.childs); });
  recorrer(cfg.supplierClassification);
  const conFicha = new Set((cfg.cards ?? []).filter(f => f.enabled).flatMap(f => f.associatedClassifications ?? []));
  let total = 0, sinClase = 0, sinFicha = 0;
  for (const [clase, n] of Object.entries(socios)) {
    total += n;
    if (!clases.has(clase)) sinClase += n;
    if (!conFicha.has(clase)) sinFicha += n;
  }
  return { total, sinClase, sinFicha };
}
export const seccionesDe = (i: { sectionCardId: string; extraSectionIds?: string[] | null }) =>
  [i.sectionCardId, ...(i.extraSectionIds ?? [])].filter(Boolean);
